import { createContext, type ComponentChildren } from 'preact';
import { useContext, useMemo } from 'preact/hooks';
import type { LocaleCode, LocaleManifest, MessageCatalog } from './types';
import { detectLocale, stripLocalePrefix, withLocalePrefix } from './routing';

/**
 * Active-locale context. Computed once per render of `<LocaleProvider>`
 * from the current pathname (browser) or the explicit `path` prop
 * (SSR/prerender). Children consume via `useLocale()` for navigation
 * helpers, or via `useTranslation()` for message access.
 *
 * Switching locales is a page navigation (window.location.assign)
 * because the active locale lives in the URL - a switch should reload
 * to pick up the new route.
 */

export interface LocaleContextValue {
    /** Currently-active locale code. */
    locale: LocaleCode;
    /** Full manifest. Use for the language switcher's list of options + metadata. */
    manifest: LocaleManifest;
    /** Catalog for the active locale (may be partial; the fallback below backfills). */
    messages: MessageCatalog;
    /** Catalog for the platform fallback locale. Always present. */
    fallbackMessages: MessageCatalog;
    /**
     * The locale to send as <c>?set=</c> on API calls, or undefined
     * when reading the default-locale partition. Default-locale reads
     * land on the source partition without a query param; non-default
     * reads use Koan's partition-aware routing (ADR-0043).
     */
    apiSet: LocaleCode | undefined;
    /**
     * Build a URL for the same canonical page in a different locale. The
     * current pathname (with the active locale's prefix stripped) is the
     * canonical input.
     */
    hrefForLocale: (target: LocaleCode) => string;
    /** Navigate to the same page in a different locale via a full page load. */
    switchLocale: (target: LocaleCode) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export interface LocaleProviderProps {
    manifest: LocaleManifest;
    /** Map from locale code to its catalog. Codes not in the map fall back. */
    catalogs: Record<LocaleCode, MessageCatalog>;
    /**
     * Override the pathname used to detect the active locale. Defaults to
     * `window.location.pathname` in the browser. Provide explicitly for
     * SSR/prerender.
     */
    path?: string;
    children: ComponentChildren;
}

export function LocaleProvider({ manifest, catalogs, path, children }: LocaleProviderProps) {
    const value = useMemo<LocaleContextValue>(() => {
        // Mount-time pathname is used only to seed the active locale (which drives the messages
        // catalog and ?set= query). Path helpers below read window.location.pathname FRESH each
        // call so client-side navigation (preact-iso pushState) doesn't trap the switcher with
        // a stale canonical path. Without this, clicking French on /emporium would route to /fr
        // (the original mount path) instead of /fr/emporium.
        const mountPathname = path ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
        const locale = detectLocale(mountPathname, manifest);
        const fallbackMessages = catalogs[manifest.fallback] ?? catalogs[manifest.defaultLocale] ?? {};
        const messages = catalogs[locale] ?? fallbackMessages;

        const livePathname = () =>
            typeof window !== 'undefined' ? window.location.pathname : mountPathname;

        return {
            locale,
            manifest,
            messages,
            fallbackMessages,
            apiSet: locale === manifest.defaultLocale ? undefined : locale,
            hrefForLocale: (target) =>
                withLocalePrefix(stripLocalePrefix(livePathname(), manifest), target, manifest),
            switchLocale: (target) => {
                if (typeof window === 'undefined') return;
                const canonical = stripLocalePrefix(window.location.pathname, manifest);
                const href = withLocalePrefix(canonical, target, manifest);
                window.location.assign(href);
            },
        };
    }, [manifest, catalogs, path]);

    return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * Access the locale context. Throws if no provider is mounted - keeps
 * the bug visible during development rather than silently rendering with
 * fallbacks.
 */
export function useLocale(): LocaleContextValue {
    const ctx = useContext(LocaleContext);
    if (!ctx) {
        throw new Error('useLocale() called outside <LocaleProvider>. Wrap your app root.');
    }
    return ctx;
}
